import { action, makeObservable, observable, override, when } from 'mobx';
import { ROTA } from '../dominio/Rota';
import ErrorUtil from '../util/ErrorUtil';
import Store from './Store';
import { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação.
 * Deve-se criar uma para cada contexto ou entidade.
 */
class ErrorStore extends Store {
    hasError = false;
    error = undefined;
    info = undefined;

    constructor(props) {
        super(props);

        makeObservable(this, {
            hasError: observable,
            error: observable,
            info: observable,
            registrarErro: action,
            reset: override
        });
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    reset() {
        this.hasError = false;
        this.error = undefined;
        this.info = undefined;
    }

    registrarErro(error, info) {
        this.hasError = true;
        this.error = error;

        if (typeof info === 'string') {
            this.info = info;
        } else if (error) {
            this.info = ErrorUtil.montarMensagemErro(error);
        } else {
            this.info = '';
        }
    }

    tratarRespostaErro(error, logusLogout) {
        this.registrarErroLog(error);
        if (this.isErroSeguranca(error)) {
            this.tratarRespostaErroSeguranca(logusLogout);
        } else if (this.isErroStream(error)) {
            this.tratarRespostaErroStream(error);
        } else if (this.isErro500(error)) {
            this.tratarRespostaErro500(error);
        } else if (this.existeMensagemErro(error)) {
            this.tratarRespostaErroOutrosStatus(error);
        } else if (this.isNetworkError(error)) {
            this.rootStore.mensagemStore.abrirMensagemErro(
                'Falha de rede. [' + ErrorUtil.montarMensagemErro(error) + ']'
            );
        } else {
            // Em qualquer outra situação, exibe a página de erro.
            this.rootStore.errorStore.registrarErro(error, error + '\n' + JSON.stringify(error.response));
        }
    }

    isNetworkError(error) {
        return error.message && error.message.toLowerCase().indexOf('network error') > -1;
    }

    existeMensagemErro(error) {
        return error.response?.data && (error.response.data.mensagem || error.response.data.mensagens);
    }

    isErro500(error) {
        return error.response && error.response.status === 500 && error.response.data;
    }

    isErroStream(error) {
        return error.response && error.response.data instanceof ArrayBuffer;
    }

    isErroSeguranca(error) {
        return error.response && (error.response.status === 403 || error.response.status === 401);
    }

    registrarErroLog(error) {
        if (error.response) {
            this.rootStore.logStore.registrarErro({
                status: error.response.status,
                url: error.response.config?.url,
                method: error.response.config?.method,
                body: error.response.config?.data,
                mensagem: error.message,
                resposta: error.response.data
            });
        } else if (error.config) {
            this.rootStore.logStore.registrarErro({ url: error.config.url, mensagem: error.message });
        }
    }

    tratarRespostaErroSeguranca(logusLogout) {
        if (window.location.pathname === ROTA.ACOMPANHAMENTO_SESSAO.rota) {
            this.rootStore.mensagemStore.abrirMensagemErro(
                'Não foi possível criar token de acesso ao sistema. Tente novamente mais tarde.'
            );
        } else {
            // Caso a resposta da requisição seja não autorizado, redireciona para o login após a mensagem de login inválido
            this.rootStore.mensagemStore.abrirMensagemErro('Login inválido');
            when(
                () => this.rootStore.mensagemStore.exibir === false,
                () => {
                    if (logusLogout) {
                        logusLogout();
                    } else {
                        window.location.reload();
                    }
                }
            );
        }
    }

    tratarRespostaErroStream(error) {
        let reader = new FileReader();
        reader.onload = () => this.exibirMensagemErroStream(reader.result);
        reader.readAsText(new Blob([error.response.data], { type: 'text/plain' }));
    }

    tratarRespostaErro500(error) {
        let mensagem = error.response.data.mensagem || 'Ocorreu um erro interno no servidor';
        if (error.response.data.causaRaiz && error.response.data.causaRaiz.indexOf(mensagem) === -1) {
            mensagem += ': ' + error.response.data.causaRaiz;
        }
        this.rootStore.mensagemStore.abrirMensagemErro(mensagem);
    }

    tratarRespostaErroOutrosStatus(error) {
        let mensagem = error.response.data.mensagem || error.response.data.mensagens;
        if (error.response.data.tipo === 'ALERTA') {
            this.rootStore.mensagemStore.abrirMensagemAlerta(mensagem);
        } else {
            this.rootStore.mensagemStore.abrirMensagemErro(mensagem);
        }
    }

    exibirMensagemErroStream(result) {
        const msgErroPadrao = 'Ocorreu um erro inesperado.';

        if (!result) {
            this.rootStore.mensagemStore.abrirMensagemErro(msgErroPadrao);
            return;
        }

        const resposta = JSON.parse(result);

        if (resposta) {
            this.rootStore.mensagemStore.abrirMensagemErro(resposta.mensagem || resposta.mensagens || msgErroPadrao);
        } else {
            this.rootStore.mensagemStore.abrirMensagemErro(msgErroPadrao);
        }
    }
}

export default ErrorStore;
